/**
 * The Library's multi-select (1.2.0): a set of asset ids plus an anchor for ranges, kept across scrolling, loading
 * more pages and opening an asset (and coming back), and cleared when the search or the filters change ("Selection
 * cleared"). The batch dialogs (X) do not use it: they take `selectedAssets` as a prop.
 *
 * The state changes are pure functions (selectionClick, selectionToggle, selectionAll), tested in
 * apps/web/test/library-selection-120.test.ts, as are the selection bar's eligibility rules and labels.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BULK_ACTION_LIMIT, tooManyAssetsMessage, type AssetSummary } from '@prism/contracts';
import { toast } from '../../components/Toasts';

/** Which keys were held on a click (the grid's and table's gestures). */
export interface SelectGesture {
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
}

export interface LibrarySelection {
  /** The selected ids, in the order they were selected. */
  ids: readonly string[];
  count: number;
  /** Where a Shift-click range starts (the last plain or Cmd/Ctrl click). */
  anchor: string | null;
  has: (id: string) => boolean;
  /** A click on a card or row: plain selects only it; Cmd/Ctrl toggles it; Shift selects the range from the anchor in `order`. */
  click: (id: string, gesture: SelectGesture, order: readonly string[]) => void;
  /** The hover checkbox and the table's checkbox column. */
  toggle: (id: string) => void;
  /** Cmd/Ctrl-A: everything loaded. */
  selectAll: (ids: readonly string[]) => void;
  clear: () => void;
  /** The selected assets among those loaded, in list order (what the selection bar and its dialogs act on). */
  selectedOf: (items: readonly AssetSummary[]) => AssetSummary[];
}

export interface SelectionState {
  ids: readonly string[];
  anchor: string | null;
}

export const EMPTY_SELECTION: SelectionState = { ids: [], anchor: null };

/**
 * A click. Plain: only this one (the anchor moves to it). Cmd/Ctrl: this one in or out (the anchor moves to it).
 * Shift: the range from the anchor to this one in `order` replaces the selection (with Cmd/Ctrl too, it is added);
 * the anchor stays. Without an anchor in `order`, Shift acts as a plain click.
 */
export function selectionClick(s: SelectionState, id: string, g: SelectGesture, order: readonly string[]): SelectionState {
  const additive = Boolean(g.metaKey || g.ctrlKey);
  if (g.shiftKey) {
    const from = s.anchor ? order.indexOf(s.anchor) : -1;
    const to = order.indexOf(id);
    if (from < 0 || to < 0) return { ids: [id], anchor: id };
    const range = order.slice(Math.min(from, to), Math.max(from, to) + 1);
    if (!additive) return { ids: range, anchor: s.anchor };
    const have = new Set(s.ids);
    return { ids: [...s.ids, ...range.filter((x) => !have.has(x))], anchor: s.anchor };
  }
  if (additive) return selectionToggle(s, id);
  return { ids: [id], anchor: id };
}

/** One in or out (a checkbox, or Cmd/Ctrl-click); the anchor moves to it. */
export function selectionToggle(s: SelectionState, id: string): SelectionState {
  return s.ids.includes(id) ? { ids: s.ids.filter((x) => x !== id), anchor: id } : { ids: [...s.ids, id], anchor: id };
}

/** Everything loaded (Cmd/Ctrl-A); the anchor stays when it is among them, else the first. */
export function selectionAll(s: SelectionState, ids: readonly string[]): SelectionState {
  return { ids: [...new Set(ids)], anchor: s.anchor && ids.includes(s.anchor) ? s.anchor : ids[0] ?? null };
}

/** The selection survives leaving the Library (opening an asset) while the search and filters are the same. */
let remembered: { key: string; state: SelectionState } | null = null;

/** `resetKey` changes when the search or filters change, which clears the selection. */
export function useLibrarySelection(resetKey: string): LibrarySelection {
  const [state, setState] = useState<SelectionState>(() => (remembered && remembered.key === resetKey ? remembered.state : EMPTY_SELECTION));
  const key = useRef(resetKey);
  const current = useRef(state);
  current.current = state;
  useEffect(() => {
    if (key.current === resetKey) return;
    key.current = resetKey;
    if (current.current.ids.length > 1) toast('Selection cleared');
    if (current.current.ids.length) setState(EMPTY_SELECTION);
  }, [resetKey]);
  useEffect(() => {
    remembered = { key: key.current, state };
  }, [state, resetKey]);

  const click = useCallback((id: string, g: SelectGesture, order: readonly string[]) => setState((s) => selectionClick(s, id, g, order)), []);
  const toggle = useCallback((id: string) => setState((s) => selectionToggle(s, id)), []);
  const selectAll = useCallback((ids: readonly string[]) => setState((s) => selectionAll(s, ids)), []);
  const clear = useCallback(() => setState(EMPTY_SELECTION), []);
  return useMemo(() => {
    const set = new Set(state.ids);
    return {
      ids: state.ids, count: state.ids.length, anchor: state.anchor,
      has: (id: string) => set.has(id),
      click, toggle, selectAll, clear,
      selectedOf: (items: readonly AssetSummary[]) => items.filter((a) => set.has(a.id)),
    };
  }, [state, click, toggle, selectAll, clear]);
}

/** What the Inspector's summary of a selection counts: how many of each kind, their total size and length. */
export function selectionSummary(assets: readonly Pick<AssetSummary, 'mediaKind' | 'status' | 'sizeBytes' | 'durationTicks' | 'availability'>[]) {
  const kinds = new Map<string, number>();
  for (const a of assets) {
    const k = a.status === 'placeholder' ? 'placeholder' : a.mediaKind ?? 'other';
    kinds.set(k, (kinds.get(k) ?? 0) + 1);
  }
  return {
    count: assets.length,
    kinds: [...kinds.entries()].sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0])),
    sizeBytes: assets.reduce((n, a) => n + (a.sizeBytes ?? 0), 0),
    durationTicks: assets.reduce((n, a) => n + (a.mediaKind === 'video' || a.mediaKind === 'audio' ? a.durationTicks ?? 0 : 0), 0),
    archived: assets.filter((a) => a.availability === 'archived').length,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// The selection bar's actions: which of the selected assets each acts on, and what its button says.

/** What the eligibility rules read of an asset. */
export type SelectionAsset = Pick<AssetSummary, 'status' | 'mediaKind' | 'availability'>;

/** An asset whose media can be exported or checked: processed (or partly), not a placeholder or a failure. */
const hasMedia = (a: SelectionAsset) => a.status === 'ready' || a.status === 'partial';

export const SELECTION_ACTIONS = {
  export: { eligible: (a: SelectionAsset) => hasMedia(a), skipped: 'have no media to export' },
  workflow: { eligible: (_a: SelectionAsset) => true, skipped: '' },
  qc: { eligible: (a: SelectionAsset) => hasMedia(a) && Boolean(a.mediaKind) && a.mediaKind !== 'graphic', skipped: 'have no media to check' },
  edit: { eligible: (a: SelectionAsset) => a.mediaKind === 'video' || a.mediaKind === 'audio' || a.mediaKind === 'image', skipped: 'are not media' },
  restore: { eligible: (a: SelectionAsset) => a.availability === 'archived', skipped: 'are not archived' },
  collection: { eligible: (_a: SelectionAsset) => true, skipped: '' },
} as const;
export type SelectionActionKind = keyof typeof SELECTION_ACTIONS;

export interface SelectionActionState<T> {
  /** The assets the action acts on. */
  eligible: T[];
  /** "Run QC on 9 of 12", "Send 3 to edit", … (`{n}` in the verb places the count, else it follows). */
  label: string;
  /** Why it is disabled (none eligible, or more than BULK_ACTION_LIMIT), else null. */
  blocked: string | null;
  /** The button's tooltip: the reason it is blocked, else what is skipped. */
  title: string;
}

/**
 * One action on the selection: it counts only the assets it can act on ("Run QC on 9 of 12 (3 have no media to
 * check)"), and is disabled when none can or more than BULK_ACTION_LIMIT would (tooManyAssetsMessage).
 */
export function selectionAction<T extends SelectionAsset>(kind: SelectionActionKind, verb: string, selected: readonly T[]): SelectionActionState<T> {
  const rule = SELECTION_ACTIONS[kind];
  const eligible = selected.filter((a) => rule.eligible(a));
  const n = eligible.length;
  const total = selected.length;
  const counted = n === total ? String(total) : `${n} of ${total}`;
  const label = verb.includes('{n}') ? verb.replace('{n}', counted) : `${verb} ${counted}`;
  const skippedText = n < total && rule.skipped ? `${total - n} ${rule.skipped}` : '';
  const blocked = n === 0
    ? `None of the selected assets can be used here${rule.skipped ? ` (they ${rule.skipped})` : ''}`
    : n > BULK_ACTION_LIMIT ? `${tooManyAssetsMessage()} (${n} selected)` : null;
  const title = blocked ?? (skippedText ? `${label} (${skippedText})` : label);
  return { eligible, label, blocked, title };
}
